import { Component, OnInit, inject, input, model, output, signal } from '@angular/core';

import { District } from '../../../core/models/district.model';
import { School } from '../../../core/models/school.model';
import { SessionStorageService } from '../../../core/services/session-storage.service';
import { ManagementUser } from '../../../core/models/management-user.model';
import { ReportsService } from '../../../core/services/reports.service';
import { NgFor, NgIf } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { NgSelectModule } from '@ng-select/ng-select';
import { SchoolTeacherFilterOptions } from '../../../core/models/filters';
import { canChangeSchool } from '../../../reports/utils';
import { updateSession } from '@sentry/core';

@Component({
  selector: 'wf-school-teacher-filter',
  templateUrl: './school-teacher-filter.component.html',
  styleUrls: ['./school-teacher-filter.component.css'],
  standalone: true,
  imports: [
    NgSelectModule,
    FormsModule
   ]
})
export class SchoolTeacherFilterComponent implements OnInit {
  private readonly sessionService = inject(SessionStorageService) ;
  private readonly reportsService = inject(ReportsService) ;

  districts = model<District[]>([]);
  schools = model<School[]>([]);
  teachers = model<ManagementUser[]>([]);
  showDistrict = signal<boolean>(true);
  showSchool = signal<boolean>(true);
  showTeacher = model<boolean | undefined>(true);
  error = model<string>('');
  // Selecting district will chain load school and teacher
  readonly chainLoad = input(false);
  readonly padding = input(true);
  readonly includeAllScope = input(true);
  filterChangeEvent = output<SchoolTeacherFilterOptions>();
  selectedDistrict = model<District | null>(null);
  selectedSchool = model<School | null>(null);
  selectedTeacher = model<ManagementUser | null>(null);
  currentUser: ManagementUser | null = null ;

  ngOnInit(): void {
    this.currentUser = this.sessionService.userData() ;
    this.showDistrict.set(this.currentUser!.isFILUser() || this.currentUser!.isCustomerServiceUser()) ;
    this.showSchool.set(canChangeSchool(this.currentUser));

    const teachers = this.teachers();
    if (this.includeAllScope() && !teachers.find(t => t.userID === 0)) {
      teachers.unshift(ManagementUser.getGenericUser());
      this.teachers.set(teachers);
    }

    // Check if we already have district, school or teacher set
    let serviceDistrict = this.reportsService.getSelectedDistrictForReports() ;
    this.districts().forEach((district) => {
      if (district.districtID === serviceDistrict.districtID) {
        this.selectedDistrict.set(district);
      }
    }) ;


    let serviceSchool = this.reportsService.getSelectedSchoolForReports() ;
    this.schools().forEach((school) => {
      if (school.schoolID === serviceSchool.schoolID)
      {
        this.selectedSchool.set(school) ;
      }
    }) ;

    let serviceTeacher = this.reportsService.getSelectedTeacherForReports() ;
    this.teachers().forEach((teacher) => {
      if (teacher.userID === serviceTeacher.userID)
      {
        this.selectedTeacher.set(teacher) ;
      }
    }) ;

    if (this.chainLoad() && this.selectedDistrict() && !this.selectedSchool()) {
      this.changeDistrict();
    } else {
      this.updateFilter(false);
    }
  }

  changeDistrict() {
    this.reportsService.getSchools(this.currentUser!, this.selectedDistrict()!.districtID).subscribe({
      next: (schools) => {
        if (this.includeAllScope()) {
          schools.unshift({ schoolID: 0, districtID: 0, name: 'All', enabled: true }) ;
        }

        this.schools.set(schools) ;
        this.selectedSchool.set(this.schools()[0] ?? null) ;

        const teachers = this.includeAllScope() ? [ManagementUser.getGenericUser()] : [];
        this.teachers.set(teachers) ;
        this.selectedTeacher.set(this.teachers()[0] ?? null) ;

        if (this.chainLoad()) {
          this.changeSchool();
        } else {
          this.updateFilter();
        }
      }
    }) ;
  }

  changeSchool() {
    this.reportsService.getAllActiveTeachers(this.currentUser!, this.selectedSchool()!.schoolID).subscribe({
      next: (teachers) => {
        if (this.includeAllScope()) {
          teachers.unshift(ManagementUser.getGenericUser()) ;
        }
        this.teachers.set(teachers) ;

        // Whenever a new school is selected, make our teachers include and 'All' and set it as selected
        this.selectedTeacher.set(teachers[0] ?? null) ;

        this.updateFilter() ;
      }
    }) ;
  }

  updateFilter(updateSession = true) {
    let filterOptions = this.getFilterSettings() ;

    if (updateSession) {
      this.sessionService.setSelectedDistrict(filterOptions.district!) ;
      this.sessionService.setSelectedSchool(filterOptions.school!) ;
      this.sessionService.setSelectedTeacher(filterOptions.teacher!) ;
    }

    this.filterChangeEvent.emit(filterOptions) ;
  }

  getFilterSettings(): SchoolTeacherFilterOptions {
    return {
      district: this.selectedDistrict(),
      school: this.selectedSchool(),
      teacher: this.selectedTeacher(),
    } ;
  }
}
